/**
 * AgentesEnEsteChat — GEN-PT-006: el bloque de contexto de la conversación
 * activa, que antes vivía en el panel derecho y ahora va a la izquierda
 * («trabajar a la izquierda»): «De qué va», «En este chat» (participantes con
 * rol y color de agente) y «Fuentes citadas» (solo si el hilo trae `sources`;
 * no se inventa el dato si no ha llegado).
 */

import { useState } from 'react'
import { AgentAvatar } from './AgentAvatar.jsx'
import { useSession } from '../context/SessionContext.jsx'
import { agentColorOf } from '../lib/agent-colors.js'
import { useFotoAgente } from '../context/FotosAgentes.jsx'
import { llamables } from '../lib/llamada.js'

function AgentPopover({ agent, onClose }) {
  const fotoDe = useFotoAgente()  // GEN-AG-006: foto en vigor de la ficha
  return (
    <div className="agent-popover" role="dialog" aria-label={agent.nombre}>
      <button className="agent-popover__close" onClick={onClose}>✕</button>
      <AgentAvatar name={agent.nombre} avatarUrl={fotoDe(agent)} size={52} />
      <div className="agent-popover__info">
        <strong>{agent.nombre}</strong>
        <span>{agent.rol}</span>
        {agent.departamento && <span className="agent-popover__bloque">{agent.departamento}</span>}
        {agent.descripcion && <p className="agent-popover__desc">{agent.descripcion}</p>}
      </div>
    </div>
  )
}

export function AgentesEnEsteChat({ activeChat, sources = [], agentesQueRespondieron = [], onAnadirAgente }) {
  const fotoDe = useFotoAgente()
  const { agents, agentPalette, resolvedTheme } = useSession()
  const [popoverAgent, setPopoverAgent] = useState(null)
  const [eligiendo, setEligiendo] = useState(false)  // GEN-HU-033: lista de «+ Añadir agente»

  // «En este chat» son los agentes que han respondido en la conversación
  // (GEN-195; salen de los turnos). Mientras nadie ha respondido (borrador),
  // el interlocutor declarado del borrador, para que el bloque no quede vacío.
  const porId = id => agents.find(ag => ag.id === id)
    ?? { id, nombre: id.charAt(0).toUpperCase() + id.slice(1), rol: null }
  const chatAgents = agentesQueRespondieron.length > 0
    ? agentesQueRespondieron.map(porId)
    : (activeChat?.agentes_en_chat ?? [])
        .map(a => agents.find(ag => ag.nombre === a.nombre) ?? { nombre: a.nombre, rol: null })

  function ringFor(agent) {
    const id = agents.find(a => a.nombre === agent.nombre)?.id
    return id ? agentColorOf(agentPalette ?? {}, id, resolvedTheme) : null
  }

  return (
    <div className="rail-section chat-context">
      <div className="rail-section__header rail-section__header--static">
        <span className="rail-section__label">Agentes en este chat</span>
      </div>
      <div className="rail-section__body">
        {activeChat?.de_que_va && (
          <p className="right-rail__de-que-va">{activeChat.de_que_va}</p>
        )}

        {chatAgents.length === 0 && <p className="rail-empty">Nadie ha intervenido aún</p>}
        <div className="right-rail__avatars">
          {chatAgents.map(agent => (
            <div key={agent.nombre} className="right-rail__avatar-wrap">
              <button
                className="right-rail__avatar-btn"
                onClick={() => setPopoverAgent(popoverAgent?.nombre === agent.nombre ? null : agent)}
                title={agent.nombre}
              >
                <AgentAvatar name={agent.nombre} avatarUrl={fotoDe(agent)} size={44} ringColor={ringFor(agent)} />
              </button>
              <span className="right-rail__avatar-info">
                <span className="right-rail__avatar-name">{agent.nombre}</span>
                {agent.rol && <span className="right-rail__avatar-role">{agent.rol}</span>}
              </span>
              {popoverAgent?.nombre === agent.nombre && (
                <AgentPopover agent={agent} onClose={() => setPopoverAgent(null)} />
              )}
            </div>
          ))}
        </div>
        {/* GEN-HU-033: no abre otro chat; deja «@Nombre » en el composer para llamarle aquí. */}
        <button
          className="add-agent-btn"
          title="Añadir un agente a esta conversación"
          aria-haspopup="menu"
          aria-expanded={eligiendo}
          onClick={() => setEligiendo(v => !v)}
        >
          + Añadir agente
        </button>
        {eligiendo && (
          <ul className="add-agent-menu" role="menu" aria-label="Agentes a los que llamar">
            {llamables(agents).length === 0 && <li className="rail-empty">No hay agentes activos</li>}
            {llamables(agents).map(ag => (
              <li key={ag.id} role="none">
                <button
                  role="menuitem"
                  className="add-agent-menu__item"
                  onClick={() => { setEligiendo(false); onAnadirAgente?.(ag) }}
                >
                  <AgentAvatar name={ag.nombre} avatarUrl={fotoDe(ag)} size={22} ringColor={ringFor(ag)} />
                  <span className="add-agent-menu__name">{ag.nombre}</span>
                  {ag.rol && <span className="add-agent-menu__role">{ag.rol}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}

        {sources.length > 0 && (
          <div className="right-rail__section">
            <p className="right-rail__label">Fuentes citadas</p>
            <div className="right-rail__sources">
              {sources.map(s => (
                <span key={s.ref ?? s.label} className="source-chip" title={s.ref}>
                  {s.label}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
